import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, PersonStandingIcon, TruckIcon } from "lucide-react";
import { add, format } from "date-fns";
import { cn, formatDateWithOrdinal } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { useGetAvailabilityQuery } from "@/store/moves/apis";
import { MOVE_KINDS, SERVICE_TYPES } from "@/constants";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { computeHourlyRate } from "./functions";
import { useToast } from "@/hooks/use-toast";

const Schedule: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const mover = useSelector(getSelectedMover);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [isAvailabilityDrawerOpen, setIsAvailabilityDrawerOpen] =
    useState(false);
  const { toast } = useToast();
  const [startTimeInput, setStartTimeInput] = useState("");
  const [endTimeInput, setEndTimeInput] = useState("");

  const startHour = watch("start_window_begin_hour");
  const startMinute = watch("start_window_begin_minute");
  const startMeridiem = watch("start_window_begin_meridiem");
  const endHour = watch("start_window_end_hour");
  const endMinute = watch("start_window_end_minute");
  const endMeridiem = watch("start_window_end_meridiem");
  const selectedDate = watch("service_date");

  const formattedDate = selectedDate
    ? format(add(selectedDate, { hours: 12 }), "yyyy-MM-dd")
    : "";

  const {
    data: availability,
    isLoading: availabilityLoading,
    isError: availabilityError,
  } = useGetAvailabilityQuery(formattedDate, {
    skip: !formattedDate,
  });

  const availabilitySummary = useMemo(() => {
    if (!availability) return null;
    return {
      moves: availability.length,
      num_of_movers: availability.reduce(
        (acc, curr) => acc + curr.num_of_movers,
        0
      ),
      num_of_trucks: availability.reduce(
        (acc, curr) => acc + curr.num_of_trucks,
        0
      ),
    };
  }, [availability]);

  const renderAvailabilitySummary = () => {
    if (availabilityLoading)
      return (
        <div className="text-sm text-muted-foreground">
          Checking schedule...
        </div>
      );

    if (availabilityError)
      return (
        <div className="text-sm text-red-500">Error fetching availability</div>
      );

    if (availability && availabilitySummary) {
      if (availability.length > 0) {
        return (
          <div className="mt-1.5 text-xs text-gray-700">
            <span
              onClick={() => setIsAvailabilityDrawerOpen(true)}
              className="underline cursor-pointer underline-offset-4 decoration-yellow-500 decoration-2"
            >
              {availabilitySummary.moves} job
              {availabilitySummary.moves > 1 ? "s" : ""}
            </span>{" "}
            ({availabilitySummary.num_of_movers} movers,{" "}
            {availabilitySummary.num_of_trucks} truck
            {availabilitySummary.num_of_trucks > 1 ? "s" : ""})<br />
            on this day
          </div>
        );
      } else {
        return (
          <div className="mt-1 text-sm text-gray-700">No moves on this day</div>
        );
      }
    }
  };

  const formatTime = (
    hour: number | null,
    minute: number | null,
    meridiem: string | null
  ) => {
    if (hour === null || minute === null || meridiem === null) {
      return "";
    }

    // Convert 24h to 12h format
    let displayHour = hour;
    if (hour > 12) {
      displayHour = hour - 12;
    } else if (hour === 0) {
      displayHour = 12;
    }

    return `${displayHour}:${minute.toString().padStart(2, "0")}${meridiem}`;
  };

  const parseTime = (timeString: string) => {
    const match = timeString.match(/^(\d{1,2})(?::?(\d{2}))?\s*(am|pm)?$/i);
    if (match) {
      const [, hour, minute, meridiem] = match;
      let parsedHour = parseInt(hour, 10);
      const parsedMinute = minute ? parseInt(minute, 10) : 0;
      const parsedMeridiem = meridiem
        ? meridiem.toUpperCase()
        : parsedHour < 12
        ? "AM"
        : "PM";

      // Keep hours in 12-hour format
      if (parsedHour > 12) {
        parsedHour = parsedHour - 12;
      } else if (parsedHour === 0) {
        parsedHour = 12;
      }

      return {
        hour: parsedHour,
        minute: parsedMinute,
        meridiem: parsedMeridiem,
      };
    }
    return {
      hour: null,
      minute: null,
      meridiem: null,
    };
  };

  const changeHourlyRate = () => {
    // first line item is labor (we add this by default when we create a move)
    if (!mover?.mover_configuration) {
      return;
    }

    const lineItems = watch("line_items");
    const numMovers = parseInt(watch("num_of_movers")?.toString() || "0");
    const date = watch("service_date");
    const rate = computeHourlyRate(numMovers, mover.labor_rates, date);
    const laborLineItem = lineItems.findIndex(
      (item: any) => item.category === "labor"
    );

    let name, description, currentRate;
    switch (mover?.mover_configuration.hourly_rate_method) {
      case "standard_hourly":
        name = "Labor Rate";
        // description = `${numMovers} movers per hour`;
        break;
      case "flat_plus_hourly":
        name = "Labor Rate";
        description = `per billed hour`;
        break;
    }
    if (laborLineItem === -1) {
      setValue("line_items", [
        ...lineItems,
        {
          id: null,
          name: name,
          description: description,
          qty: null,
          price: rate,
          subtotal: null,
          mover_fee_id: null,
          type: "frozen",
          rate_qualifier: null,
          discount: 0,
          category: "labor",
          nature: "charge",
          created_at: null,
          updated_at: null,
        },
      ]);
    } else {
      currentRate = watch(`line_items.${laborLineItem}.price`);
      setValue(`line_items.${laborLineItem}.price`, rate);
      setValue(`line_items.${laborLineItem}.description`, description);
      setValue("hourly_rate", rate);
    }

    toast({
      title:
        "Labor Rate changed from $" +
        Math.floor(currentRate || 0) +
        "/hr to $" +
        rate +
        "/hr",
      className: "bg-green-600 text-white",
    });
  };

  useEffect(() => {
    if (startHour !== null && startMinute !== null && startMeridiem !== null) {
      setStartTimeInput(formatTime(startHour, startMinute, startMeridiem));
    }
  }, [startHour, startMinute, startMeridiem]);

  useEffect(() => {
    if (endHour !== null && endMinute !== null && endMeridiem !== null) {
      setEndTimeInput(formatTime(endHour, endMinute, endMeridiem));
    }
  }, [endHour, endMinute, endMeridiem]);

  const handleTimeChange = (timeString: string, isStartTime: boolean) => {
    if (isStartTime) {
      setStartTimeInput(timeString);
    } else {
      setEndTimeInput(timeString);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row gap-4">
        <FormField
          control={control}
          name="service_date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel className="flex flex-row items-center gap-2">
                Date
                <FormMessage />
              </FormLabel>
              <div className="flex flex-row items-center gap-4">
                <Popover
                  open={datePopoverOpen}
                  onOpenChange={setDatePopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-[130px] pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(add(field.value, { hours: 12 }), "MM/dd/yyyy")
                        ) : (
                          <span>Date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={
                        typeof field.value === "string"
                          ? new Date(field.value + "T12:00:00")
                          : field.value
                      }
                      onSelect={(date) => {
                        field.onChange(
                          date ? format(date, "yyyy-MM-dd") : date
                        );
                        changeHourlyRate();
                        setDatePopoverOpen(false);
                      }}
                      disabled={(date) => {
                        const yesterday = new Date(
                          new Date().setDate(new Date().getDate() - 1)
                        );
                        return date < yesterday;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {isAvailabilityDrawerOpen && (
                  <Sheet
                    open={isAvailabilityDrawerOpen}
                    onOpenChange={setIsAvailabilityDrawerOpen}
                  >
                    <SheetContent>
                      <SheetHeader>
                        <SheetTitle>
                          Booked Moves for {formatDateWithOrdinal(field.value)}
                        </SheetTitle>
                        <SheetDescription>
                          {" "}
                          You have {availabilitySummary?.moves} moves booked on
                          this day.
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-4 flex flex-col gap-3 max-h-[80vh] overflow-y-auto">
                        {availability?.map((move) => (
                          <div
                            className="p-2 border border-gray-400"
                            key={move.id}
                          >
                            <div className="flex flex-row justify-between items-center">
                              <div className="text-base font-medium">
                                {SERVICE_TYPES[
                                  move.service as keyof typeof SERVICE_TYPES
                                ] ||
                                  move.service ||
                                  "N/A"}
                              </div>
                              <div className="flex flex-row items-center gap-1 text-sm">
                                <PersonStandingIcon className="w-4 h-4" />
                                {move.num_of_movers} movers
                                <TruckIcon className="ml-2 w-4 h-4" />
                                {move.num_of_trucks} trucks
                              </div>
                            </div>
                            <div>
                              {move.start_window_begin_hour}
                              {move.start_window_begin_minute
                                ? `:${move.start_window_begin_minute}`
                                : ""}
                              {move.start_window_begin_meridiem} to{" "}
                              {move.start_window_end_hour + move.hours_estimate}
                              {move.start_window_end_minute
                                ? `:${move.start_window_end_minute}`
                                : ""}
                              {move.start_window_end_hour +
                                move.hours_estimate >=
                              12
                                ? "PM"
                                : "AM"}{" "}
                              ({Math.floor(move.hours_estimate)} hour
                              {Math.floor(move.hours_estimate) !== 1 ? "s" : ""}
                              )
                            </div>
                            <div className="text-sm text-gray-500">
                              {move.origin.city}
                              {move.destination?.city &&
                                ` to ${move.destination.city}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {MOVE_KINDS[move.kind as keyof typeof MOVE_KINDS]}{" "}
                              {move.kind === "apartment" || move.kind === "sfh"
                                ? `${move.num_of_rooms}BR`
                                : ""}
                            </div>
                          </div>
                        ))}
                      </div>
                    </SheetContent>
                  </Sheet>
                )}
              </div>
              {renderAvailabilitySummary()}
            </FormItem>
          )}
        />

        <div className="">
          <FormLabel className="flex flex-row items-center gap-2">
            Arrival Window
            {(errors.start_window_begin || errors.start_window_end) && (
              <FormMessage>
                {" "}
                {errors.start_window_begin?.message?.toString()}
              </FormMessage>
            )}
          </FormLabel>
          <div className="flex flex-row items-center gap-2">
            <FormField
              control={control}
              name="start_window_begin"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      value={startTimeInput}
                      onChange={(e) => handleTimeChange(e.target.value, true)}
                      onBlur={() => {
                        const parsed = parseTime(startTimeInput);
                        if (parsed) {
                          setValue("start_window_begin_hour", parsed.hour);
                          setValue("start_window_begin_minute", parsed.minute);
                          setValue(
                            "start_window_begin_meridiem",
                            parsed.meridiem
                          );
                          setStartTimeInput(
                            formatTime(
                              parsed.hour,
                              parsed.minute,
                              parsed.meridiem
                            )
                          );
                        }
                      }}
                      className="w-20"
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <span>to</span>
            <FormField
              control={control}
              name="start_window_end"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      value={endTimeInput}
                      onChange={(e) => handleTimeChange(e.target.value, false)}
                      onBlur={() => {
                        const parsed = parseTime(endTimeInput);
                        if (parsed) {
                          setValue("start_window_end_hour", parsed.hour);
                          setValue("start_window_end_minute", parsed.minute);
                          setValue(
                            "start_window_end_meridiem",
                            parsed.meridiem
                          );
                          setEndTimeInput(
                            formatTime(
                              parsed.hour,
                              parsed.minute,
                              parsed.meridiem
                            )
                          );
                        }
                      }}
                      className="w-20"
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <span className="font-normal text-xs">(eg 8am, 130pm)</span>
        </div>

        <div>
          <FormLabel className="flex flex-row items-center gap-2">
            Movers
            {errors.num_of_movers && (
              <FormMessage>
                {" "}
                {errors.num_of_movers.message?.toString()}
              </FormMessage>
            )}
          </FormLabel>
          <FormField
            control={control}
            name="num_of_movers"
            render={({ field }) => (
              <FormItem className="flex flex-row items-baseline">
                <FormControl>
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-r-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        if (currentValue > 0) {
                          field.onChange(currentValue - 1);
                          changeHourlyRate();
                        }
                      }}
                    >
                      -
                    </Button>
                    <Input
                      className="w-12 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        if (e.target.value === "") field.onChange(null);
                        else if (!isNaN(parseInt(e.target.value))) {
                          field.onChange(parseInt(e.target.value));
                          changeHourlyRate();
                        }
                      }}
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-l-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        field.onChange(currentValue + 1);
                        changeHourlyRate();
                      }}
                    >
                      +
                    </Button>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormLabel className="flex flex-row items-center gap-2">
            Trucks
            {errors.num_of_trucks && (
              <FormMessage>
                {" "}
                {errors.num_of_trucks.message?.toString()}
              </FormMessage>
            )}
          </FormLabel>
          <FormField
            control={control}
            name="num_of_trucks"
            render={({ field }) => (
              <FormItem className="flex flex-row items-baseline">
                <FormControl>
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-r-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        if (currentValue > 0) {
                          field.onChange(currentValue - 1);
                        }
                      }}
                    >
                      -
                    </Button>
                    <Input
                      className="w-12 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        if (e.target.value === "") {
                          field.onChange(null);
                        } else if (!isNaN(parseInt(e.target.value))) {
                          field.onChange(parseInt(e.target.value));
                        }
                      }}
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-l-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        field.onChange(currentValue + 1);
                        changeHourlyRate();
                      }}
                    >
                      +
                    </Button>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormLabel className="flex flex-row items-center gap-2">
            Estimated Hours
            {errors.hours_estimate && (
              <FormMessage>
                {" "}
                {errors.hours_estimate.message?.toString()}
              </FormMessage>
            )}
          </FormLabel>
          <FormField
            control={control}
            name="hours_estimate"
            render={({ field }) => (
              <FormItem className="flex flex-row items-baseline">
                <FormControl>
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-r-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        if (currentValue > 0) {
                          field.onChange(currentValue - 1);
                        }
                      }}
                    >
                      -
                    </Button>
                    <Input
                      className="w-12 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      type="number"
                      step="0.01"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === ""
                            ? ""
                            : parseFloat(e.target.value)
                        )
                      }
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 w-8 rounded-l-none px-0"
                      onClick={() => {
                        const currentValue = field.value ?? 0;
                        field.onChange(currentValue + 1);
                      }}
                    >
                      +
                    </Button>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default Schedule;
